import { Bomb, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { getDraftBoardPosColor } from '@/lib/nflDraftBoardColors';
import TeamLogo from '../TeamLogo';
import { draftableSlots } from '@/lib/nflRosterSlots';

/**
 * Prompt 3: the left roster rail.
 *
 * ── SLOT ASSIGNMENT COMES FROM THE SERVER (SAB-842 / SAB-739) ───────────────
 *
 * `slotAssignments` is playerId -> slot, taken from nfl_rosters.position_slot, which
 * nfl_acquire_pick_lock wrote at pick time against nfl_slot_eligibility. This component does
 * NOT re-derive it from the player's position, and must not start:
 *
 *   - it would be a second, divergent copy of NFL's position->slot rules in the client,
 *     which is the duplication SAB-739 deleted;
 *   - the naive match is wrong anyway. FLEX matches no player's position, so it would sit
 *     permanently empty, and every IDP variant (SAF/OLB/NT/MLB/DE/DT...) would fall through
 *     to the bench.
 *
 * ── IR IS IN THE SLOT LIST ON PURPOSE ───────────────────────────────────────
 *
 * The design lists 11 slots and stops at bench. Prod holds 120 rows at IR1/IR2. Omitting IR
 * would make those players vanish from their own manager's roster view while still occupying
 * a roster spot -- so the slot order comes from the caller, not from this file.
 *
 * SAB-886: that caller now passes THIS LEAGUE'S slots, derived by `leagueSlotOrder`, rather
 * than a 21-slot constant. This component renders `slotOrder.map(...)`, so the list it is
 * handed decides whether a row exists at all: under the old constant, which had no FLEX2, a
 * player the server assigned to FLEX2 had no row and disappeared from their own roster.
 *
 * SAB-986: the ROWS are still every slot -- including IR, so a player parked there stays
 * visible -- but the HEADER COUNT is not. It counts `draftableSlots(slotOrder)`, which
 * drops IR. This paragraph previously said the count "must be the league's slot count";
 * that was wrong in the same way the 21-slot constant was, just less visibly: a draft can
 * never fill IR, so counting it made every finished team read 17 / 18 forever.
 */

export interface RailTeam {
  id: string;
  name: string;
  /**
   * SAB-918: the team's assigned snake slot, or NULL when no draft order exists yet.
   * Previously a required number fed from the caller's ARRAY INDEX, which printed a slot
   * that contradicted the team's real draft_position. Nullable so "no order assigned" has
   * a representation other than a wrong number.
   */
  draftSlot: number | null;
  rosterCount: number;
  /** SAB-1004: the team's logo, or null for the monogram fallback. */
  logoUrl?: string | null;
  isMine?: boolean;
  isOnTheClock?: boolean;
  /** SAB-845 surface: "Pick skipped in R2". */
  skippedInRound?: number | null;
  /** "next at 2.04", user only. */
  nextPickLabel?: string | null;
}

export interface RailPlayer {
  id: string;
  full_name: string;
  position: string;
  nfl_team?: string | null;
  pickLabel?: string | null;
}

export interface RosterRailProps {
  teams: RailTeam[];
  selectedTeamId: string | null;
  onSelectTeam: (teamId: string) => void;
  /** Explicit, already-expanded slot list. Includes IDP and IR. */
  slotOrder: string[];
  /** playerId -> slot name, authoritative, from the server. */
  slotAssignments: Record<string, string>;
  /** Players on the selected team, by id. */
  playersById: Map<string, RailPlayer>;
  /** Player ids on the selected roster. */
  rosterPlayerIds: string[];
  /** Footer, hidden when viewing another team. */
  biggestNeed?: { position: string; slotsOpen: number; poolCount: number } | null;
}

export default function RosterRail({
  teams, selectedTeamId, onSelectTeam, slotOrder, slotAssignments,
  playersById, rosterPlayerIds, biggestNeed,
}: RosterRailProps) {
  const selected = teams.find(t => t.id === selectedTeamId) ?? null;
  const viewingOwn = selected?.isMine === true;
  const idx = teams.findIndex(t => t.id === selectedTeamId);

  // slot -> playerId, inverted from the server's authoritative map, restricted to this roster.
  const bySlot = new Map<string, string>();
  for (const pid of rosterPlayerIds) {
    const slot = slotAssignments[pid];
    if (slot) bySlot.set(slot, pid);
  }

  const step = (delta: number) => {
    if (teams.length === 0) return;
    // Wraps, per the design.
    const next = ((idx + delta) % teams.length + teams.length) % teams.length;
    onSelectTeam(teams[next].id);
  };

  // SAB-986: count against DRAFTABLE capacity, not every slot. `rosterPlayerIds.length`
  // was also the wrong numerator: it counts players, so a player the pick engine parked on
  // IR (its assignment waterfall ends at IR) would have been counted against a denominator
  // that no longer includes IR, printing 18 / 17. Both sides are now the same slot set.
  const draftable = draftableSlots(slotOrder);
  const filled = draftable.filter(s => bySlot.has(s)).length;

  return (
    <aside
      data-testid="roster-rail"
      /* SAB-882: min-w-0, the roster half of SAB-879.
         SAB-877 made this cell a `grid` so the rail would stretch vertically; that also made
         the rail a GRID ITEM, whose automatic minimum size is its min-content -- so with a
         real roster (19 filled slots, real player names, position + team + pick metadata) it
         renders 289px inside its 236px track and spills 53px into the centre column.
         It does not LOOK like the rail overflowing, because the rail is grid child 0 and
         <main> is child 1: <main> paints on top. In BOARD view its opaque Card covers the
         spill, which reads as "the board panel covers the rail's right edge"; the
         PLAYERS/BOARD toggle at x=254 sits inside the 236-289 band and paints over it too.
         In PLAYERS there is no Card, so the spill is simply visible and the rail looks wider.
         One clamp, all of it.
         This was in PR #307 and I removed it, having "measured" no overflow with a
         stub-shaped rail full of `Roster Player 0`. That fixture could not exhibit the bug.
         The check that ships with this one is run against the stub too, and is required to
         MISS there -- see the blindness control in the PR. */
      className="min-h-0 min-w-0 flex flex-col border-r border-border bg-card"
    >
      <div className="flex-none flex flex-col gap-2 border-b border-border px-3 py-2.5">
        <div className="flex items-center justify-between">
          <span className="font-display font-bold text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
            Rosters
          </span>
          <span className="font-display font-bold text-[11px] tabular-nums text-muted-foreground">
            {filled} / {draftable.length}
          </span>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="team-selector"
              className="flex h-[34px] w-full items-center gap-2.5 rounded-[9px] border border-border bg-muted px-2.5"
            >
              {/* SAB-1004: the identity dot becomes the team's logo. The own-team cue moves
                  to the ring, so nothing is lost when a team has no logo -- the monogram
                  still sits inside a ring only your own team gets. */}
              <TeamLogo
                logoUrl={selected?.logoUrl}
                name={selected?.name}
                className={cn(
                  'h-5 w-5',
                  viewingOwn && 'ring-1 ring-primary',
                )}
                textClassName="text-[9px]"
              />
              <span className="min-w-0 flex-1 truncate text-left text-[12.5px] font-bold text-white">
                {selected?.name ?? 'Select team'}
              </span>
              <ChevronDown className="h-4 w-4 flex-none text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[260px]">
            {teams.map(t => (
              <DropdownMenuItem
                key={t.id}
                data-testid="team-option"
                onSelect={() => onSelectTeam(t.id)}
                className="flex items-start gap-2.5"
              >
                <span
                  className={cn(
                    'mt-0.5 h-5 w-5 flex-none rounded-full',
                    t.isMine ? 'bg-[image:var(--gradient-sabotage)]' : 'bg-muted-foreground/30',
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="truncate text-[12.5px] font-bold text-white">{t.name}</span>
                    {t.isMine && (
                      <span className="font-display text-[9px] font-bold tracking-wide text-primary">YOU</span>
                    )}
                  </span>
                  <span className="block text-[10.5px] text-muted-foreground">
                    {t.draftSlot != null ? `Slot ${t.draftSlot} · ` : ''}{t.rosterCount} players
                  </span>
                  {t.isOnTheClock && (
                    <span className="block text-[10.5px] font-semibold text-gold">On the clock</span>
                  )}
                  {t.skippedInRound != null && (
                    <span className="flex items-center gap-1 text-[10.5px] font-semibold text-damage">
                      <Bomb className="h-3 w-3" /> Pick skipped in R{t.skippedInRound}
                    </span>
                  )}
                  {t.isMine && t.nextPickLabel && (
                    <span className="block text-[10.5px] font-semibold text-primary">
                      next at {t.nextPickLabel}
                    </span>
                  )}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="flex items-center gap-1.5">
          <Button
            variant="outline" size="icon" aria-label="Previous team"
            onClick={() => step(-1)} className="h-6 w-[26px] rounded-[7px]"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="flex-1 text-center text-[11px] text-muted-foreground">
            Team {idx >= 0 ? idx + 1 : '—'} of {teams.length}
          </span>
          <Button
            variant="outline" size="icon" aria-label="Next team"
            onClick={() => step(1)} className="h-6 w-[26px] rounded-[7px]"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* min-h-0 is what keeps the footer pinned inside the column instead of being pushed
          past the bottom by a long roster. */}
      <div data-testid="roster-slots" className="flex-1 min-h-0 overflow-y-auto p-2.5 flex flex-col gap-1">
        {slotOrder.map(slot => {
          const pid = bySlot.get(slot);
          const p = pid ? playersById.get(pid) : undefined;
          const c = getDraftBoardPosColor(p?.position ?? slot.replace(/\d+$/, ''));
          return (
            <div
              key={slot}
              data-testid="roster-slot"
              data-slot={slot}
              data-filled={p ? 'true' : 'false'}
              className={cn(
                'flex items-center gap-2 rounded-lg px-2.5 py-[7px]',
                p ? 'bg-muted' : 'border border-dashed border-border',
              )}
            >
              <span
                className={cn(
                  'inline-flex h-[19px] w-[34px] flex-none items-center justify-center rounded border font-display text-[9px] font-bold',
                  p ? cn(c.text, c.border, c.bgFaint) : 'border-border/20 bg-muted/10 text-muted-foreground',
                )}
              >
                {slot}
              </span>
              {p ? (
                <span className="min-w-0">
                  <span className="block truncate text-[12.5px] font-semibold text-white">{p.full_name}</span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {[p.nfl_team, p.pickLabel].filter(Boolean).join(' · ')}
                  </span>
                </span>
              ) : (
                <span className="text-[12.5px] text-muted-foreground">Empty</span>
              )}
            </div>
          );
        })}
      </div>

      {viewingOwn && biggestNeed && (
        <div data-testid="biggest-need" className="flex-none border-t border-border bg-background px-3.5 py-3">
          <div className="font-display text-[10px] font-bold uppercase tracking-[0.08em] text-damage">
            Biggest need
          </div>
          <div className="font-display text-[13px] font-bold text-white">
            {biggestNeed.position} · {biggestNeed.slotsOpen} slot{biggestNeed.slotsOpen === 1 ? '' : 's'} open
          </div>
          <div className="text-[11.5px] text-muted-foreground">
            {biggestNeed.poolCount} trash-tier {biggestNeed.position}s still on the board.
          </div>
        </div>
      )}
    </aside>
  );
}
